import { loadPage, updateForm, resetCalculatedValues, setPeriodFeeLoading } from "./calculatorSlice";
import { AppThunk } from "../../app/store";
import { selectAppContext } from "../../app/slices/appSelectors";
import { fetchFinancingStructureAsync, fetchPeriodFeeAsync } from "./calculatorAsyncActions";
import { CalculatorPageState } from "./models/CalculatorPageState";
import { PeriodFeeRequestModel } from "../../api/models/PeriodFeeRequestModel";
import { parseFormattedNumber } from "../../utils/formatters";
import { Action, ThunkDispatch } from "@reduxjs/toolkit";
import { AppState } from "../../app/slices/models/appState";
import { selectPageState } from "./calculatorSelectors";
import debounce from "lodash.debounce";

const DEBOUNCE_DELAY = import.meta.env.VITE_DEBOUNCE_DELAY;

const debouncedFetchPeriodFee = debounce((dispatch, periodFeeRequestModel: PeriodFeeRequestModel) => {
  dispatch(fetchPeriodFeeAsync({ model: periodFeeRequestModel }));
}, DEBOUNCE_DELAY);

// PartialThunk is used to separate part of thunk logic for it to be reused by other thunks. Its unique trait - it uses dispatch and/or getState.
const updateOrResetCalculatedValuesPartialThunk = (
  dispatch: ThunkDispatch<
    {
      app: AppState;
      calculator: CalculatorPageState;
    },
    unknown,
    Action<string>
  >,
  getState: () => {
    app: AppState;
    calculator: CalculatorPageState;
  }
) => {
  const state = selectPageState(getState());
  if (
    !state.form.validationErrors &&
    state.form.purchasePrice &&
    state.form.downPayment !== null &&
    state.form.downPayment !== undefined &&
    state.form.contractPeriodInMonths &&
    state.form.residualValuePercentage &&
    state.financingStructure
  ) {
    const periodFeeRequestModel: PeriodFeeRequestModel = {
      purchasePrice: state.form.purchasePrice,
      downPayment: state.form.downPayment,
      contractLengthInMonths: state.form.contractPeriodInMonths,
      residualValuePercentage: state.form.residualValuePercentage,
      financingStructureId: state.financingStructure.id,
    };
    dispatch(setPeriodFeeLoading(true));
    debouncedFetchPeriodFee(dispatch, periodFeeRequestModel);
  } else {
    dispatch(resetCalculatedValues());
  }
};

export const loadPageThunk = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  const appContext = selectAppContext(state);
  await dispatch(fetchFinancingStructureAsync());
  dispatch(loadPage(appContext));
  updateOrResetCalculatedValuesPartialThunk(dispatch, getState);
};

export const updateFormThunk =
  ({ name, value }: { name: string; value: string | number | null }): AppThunk =>
  (dispatch) => {
    value = parseFormattedNumber(value);
    dispatch(updateForm({ name, value }));
  };

export const calculatePeriodFeeThunk = (): AppThunk => async (dispatch, getState) => {
  updateOrResetCalculatedValuesPartialThunk(dispatch, getState);
};
